import { defineStore } from 'pinia';
import { ref, watch } from 'vue';
import { exposuresService } from '~/services/exposures-service';
import type { RapidResponseEscalation } from '~/services/rapid-response-service';
import { rapidResponseService } from '~/services/rapid-response-service';
import { usePersonStore } from '~/stores/person';
import { useCompaniesStore } from '~/stores/person/companies';
import type { Company } from '~/types/person/companies';
import type { Exposure } from '~/types/person/details';

export const useExposureSidebarStore = defineStore('exposure-sidebar', () => {
    const selectedExposure = ref<string>();
    const exposure = ref<Exposure>();
    const personStore = usePersonStore();
    const companiesStore = useCompaniesStore();
    const { companies } = storeToRefs(companiesStore);

    const { showSuccess, showError, showWarning } = useGlobalToast();

    // escalation state
    const escalation = ref<RapidResponseEscalation | null>(null);
    const hasEscalationBegan = ref(false);
    const isBeginEscalationLoading = ref(false);
    const isFetchingEscalation = ref(false);
    const escalationTimer = ref('00:00');
    const escalationStartTime = ref<Date | null>(null);
    //to destroy the interval
    const escalationTimerInterval = ref<NodeJS.Timeout | null>(null);
    
    // License status
    const hasSignatureLicense = ref<boolean | null>(null);
    const licenseStatusMessage = ref('');
    const isFetchingLicenseStatus = ref(false);

    const company = computed(() => {
        return companies.value.find((c: Company) => c.id === exposure.value?.company_id);
    })

    async function beginEscalation(escalationNotes?: string) {
        if (!exposure.value?.id || !personStore.person?.id) {
            showError('Error', 'No exposure selected or user not found');
            return;
        }

        isBeginEscalationLoading.value = true;

        try {
            const newEscalation = await rapidResponseService.createEscalation(personStore.person.id, {
                exposure_id: exposure.value.id, // Keep as string since it's a UUID
                escalation_notes: escalationNotes,
            });

            // Refresh the exposure data to get the updated escalation
            await fetchExposure(exposure.value.id);
            loadEscalationFromExposure();
            
            showSuccess('Success', 'Rapid Response escalation has been initiated');
        } catch (error: any) {
            showError('Error', error.message || 'Failed to create escalation');
        } finally {
            isBeginEscalationLoading.value = false;
        }
    }

    function updateEscalationTimer() {
        //every 1 second update escamation timer by 1 second, return 'mm:ss'
        escalationTimerInterval.value = setInterval(() => {
            if (!escalationStartTime.value) return;

            const now = new Date();
            const timeDiff = now.getTime() - escalationStartTime.value.getTime();
            let minutes: number | string = Math.floor((timeDiff / (1000 * 60)) % 60);
            let seconds: number | string = Math.floor((timeDiff / 1000) % 60);

            //if minutes is less than 10, add a 0 in front of it
            if (minutes < 10) {
                minutes = `0${minutes}`;
            }

            //if seconds is less than 10, add a 0 in front of it
            if (seconds < 10) {
                seconds = `0${seconds}`;
            }

            escalationTimer.value = `${minutes}:${seconds}`;
        }, 1000);
    }

    function loadEscalationFromExposure() {
        if (exposure.value?.rapid_response) {
            escalation.value = exposure.value.rapid_response;
            hasEscalationBegan.value = true;
            escalationStartTime.value = new Date(escalation.value.created_at);
            updateEscalationTimer();
        } else {
            escalation.value = null;
            hasEscalationBegan.value = false;
        }
    }

    function checkLicenseStatus() {
        if (!personStore.person) {
            hasSignatureLicense.value = false;
            licenseStatusMessage.value = 'User not authenticated';
            return;
        }

        // Check if the person has any active license with SST remediation type
        const hasActiveLicense = personStore.person.licenses?.some(license => 
            license.remediation_type === 'sst' && !license.expired
        ) || false;

        hasSignatureLicense.value = hasActiveLicense;
        licenseStatusMessage.value = hasActiveLicense 
            ? 'Rapid Response escalation is available' 
            : 'Rapid Response escalation requires a Signature Services license';
    }

    function abortEscalation() {
        // Note: This doesn't cancel the actual escalation, just resets the UI state
        // In a real implementation, you might want to add an API call to cancel escalation
        hasEscalationBegan.value = false;
        escalationTimer.value = '00:00';
        escalationStartTime.value = null;
        escalation.value = null;
        if (escalationTimerInterval.value) {
            clearInterval(escalationTimerInterval.value);
            escalationTimerInterval.value = null;
        }
    }

    // Action to fetch exposure by ID
    async function fetchExposure(exposureId: string) {
        if (!exposureId || !personStore.person?.id) return;
        
        try {
            exposure.value = undefined;
            exposure.value = await exposuresService.getExposure(personStore.person.id, exposureId);
        } catch (error) {
            console.error('Failed to fetch exposure details:', error);
            exposure.value = undefined;
        }
    }

    // Watcher to automatically fetch exposure when selectedExposure changes
    watch(selectedExposure, async (exposureId) => {
        if (exposureId) {
            try {
                await fetchExposure(exposureId);
                loadEscalationFromExposure();
                checkLicenseStatus();
                
                if (exposure.value?.company_id) {
                    companiesStore.getCompanyById(exposure.value.company_id);
                }
            } catch (error) {
                console.error('Failed to fetch exposure:', error);
            }
        } else {
            exposure.value = undefined;
            escalation.value = null;
            hasEscalationBegan.value = false;
            hasSignatureLicense.value = null;
            licenseStatusMessage.value = '';
            if (escalationTimerInterval.value) {
                clearInterval(escalationTimerInterval.value);
                escalationTimerInterval.value = null;
            }
        }
    });

    // Watch for changes in person data to update license status
    watch(() => personStore.person, () => {
        checkLicenseStatus();
    }, { deep: true });

    return {
        selectedExposure,
        exposure,
        company,
        escalation,
        hasEscalationBegan,
        isBeginEscalationLoading,
        isFetchingEscalation,
        beginEscalation,
        loadEscalationFromExposure,
        abortEscalation,
        escalationTimer,
        fetchExposure,
        checkLicenseStatus,
        hasSignatureLicense,
        licenseStatusMessage,
        isFetchingLicenseStatus,
    };
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useExposureSidebarStore, import.meta.hot))
}